/*
 * Flash Cream funnel — Judge.me "Review Snippets" block, Figma reviews strip.
 * Loaded only for templates/product.flash-cream.json (see layout/theme.liquid).
 *
 * Judge.me's v2 widget renders the reviews after page load as cards in a
 * horizontally scrolling track (.jm-carousel__track). This script only
 * decorates what Judge.me renders:
 *  - an avatar with the reviewer's initial (Figma 1:134)
 *  - dots (Figma 1:136) that follow and scroll the track (Judge.me's arrows
 *    are hidden in flash-cream.css)
 * If Judge.me's markup isn't there, it does nothing.
 */
(() => {
  const WIDGET = '.jdgm-review-snippet-widget-v2';
  const CARD = '.jdgm-rev-snippet-card';

  function addAvatars(widget) {
    widget.querySelectorAll(CARD).forEach((card) => {
      if (card.querySelector(':scope > .fc-jdgm-avatar')) return;
      const name = (card.querySelector('.jdgm-rev-snippet-card__reviewer-name')?.textContent || '').trim();
      if (!name) return;
      const avatar = document.createElement('span');
      avatar.className = 'fc-jdgm-avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = Array.from(name)[0].toLocaleUpperCase();
      card.prepend(avatar);
    });
  }

  function addDots(widget) {
    const inner = widget.querySelector('.jdgm-rev-snippet-widget-v2__inner');
    const track = widget.querySelector('.jm-carousel__track');
    if (!inner || !track) return;
    const cards = Array.from(track.querySelectorAll(`:scope > ${CARD}`));
    const existing = inner.querySelector(':scope > .fc-jdgm-dots');

    if (existing && existing.childElementCount === cards.length && existing._track === track) return;
    existing?._observer?.disconnect();
    existing?.remove();
    if (cards.length < 2) return;

    const dots = document.createElement('div');
    dots.className = 'fc-jdgm-dots';
    dots._track = track;

    const setActive = (index) => {
      Array.from(dots.children).forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    };

    cards.forEach((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'fc-jdgm-dot';
      dot.setAttribute('aria-label', `${i + 1} / ${cards.length}`);
      dot.addEventListener('click', () => {
        // Scroll only the track (not the page) to the card's position.
        const trackBox = track.getBoundingClientRect();
        const cardBox = card.getBoundingClientRect();
        track.scrollBy({ left: cardBox.left - trackBox.left, behavior: 'smooth' });
        setActive(i);
      });
      dots.append(dot);
    });

    // The visible card follows Judge.me's autoplay, swipes and dot clicks.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(cards.indexOf(entry.target));
        });
      },
      { root: track, threshold: 0.6 }
    );
    cards.forEach((card) => observer.observe(card));
    dots._observer = observer;

    inner.append(dots);
    setActive(0);
  }

  // Review popup (opened by clicking a card): Judge.me adds it to <body>
  // after the click, so look for it for a short while and add the avatar.
  function decoratePopupSoon() {
    const started = Date.now();
    const check = () => {
      const popup = document.querySelector('.jdgm-rev-snippet-lightbox');
      if (popup) {
        if (!popup.querySelector(':scope > .fc-jdgm-avatar')) {
          const name = (popup.querySelector('.jdgm-rev-snippet-card__reviewer-name')?.textContent || '').trim();
          if (name) {
            const avatar = document.createElement('span');
            avatar.className = 'fc-jdgm-avatar';
            avatar.setAttribute('aria-hidden', 'true');
            avatar.textContent = Array.from(name)[0].toLocaleUpperCase();
            popup.prepend(avatar);
          }
        }
        return;
      }
      // The first open also loads Judge.me's popup code, so allow a few seconds.
      if (Date.now() - started < 8000) setTimeout(check, 100);
    };
    check();
  }

  function watchPopup(root) {
    const open = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest(`${WIDGET} ${CARD}`)) decoratePopupSoon();
    };
    root.addEventListener('click', open);
    root.addEventListener('keydown', open);
  }

  // Rating badge: show the average with one decimal, as in Figma ("5.0").
  function formatAverage(root) {
    root.querySelectorAll('.jdgm-prev-badge[data-average-rating]').forEach((badge) => {
      const value = parseFloat(badge.dataset.averageRating);
      if (Number.isFinite(value)) badge.dataset.fcAverage = value.toFixed(1);
    });
  }

  function enhance(root) {
    formatAverage(root);
    root.querySelectorAll(WIDGET).forEach((widget) => {
      if (!widget.querySelector(CARD)) return;
      addAvatars(widget);
      addDots(widget);
    });
  }

  function init() {
    const root = document.querySelector('#main .main-product .product-text');
    if (!root) return;
    enhance(root);
    watchPopup(root);
    // Judge.me renders asynchronously; the theme editor also re-renders blocks.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        enhance(root);
      });
    }).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
